import React, { useState, useEffect, useMemo } from 'react';
import { modifiedTokenHash } from '../../Utils/utils';

const SearchComponent = ({ data = [], readOnly, autoFocus, searchIcon }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);

  const MIN_QUERY_LENGTH = 2;   // don't run full includes-search until user typed >= 2 chars
  const MAX_RESULTS = 20;      // show at most this many results

  // Precompute normalized searchable fields to speed up repeated filtering
  const normalized = useMemo(() => {
    return (data || []).map(item => ({
      orig: item,
      name: String(item.TokenName ?? '').trim().toLowerCase(),
      symbol: String(item.TokenSymbol ?? '').trim().toLowerCase(),
      pair: String(item.PairAddress ?? '').trim().toLowerCase(),
      hash: String(item.TokenHash ?? '').trim().toLowerCase()
    }));
  }, [data]);

  // Top suggestions (very fast) shown for very short queries
  const topSuggestions = useMemo(() => (normalized.slice(0, 5).map(n => n.orig)), [normalized]);

  // Debounce search input by 100ms (to prevent searching on every keystroke)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const q = String(searchQuery || '').trim().toLowerCase();
      if (q.length === 0) {
        setFilteredResults([]);
        return;
      }
      if (q.length < MIN_QUERY_LENGTH) {
        // very short query: show small suggestion set and avoid expensive includes scans
        setFilteredResults(topSuggestions);
        return;
      }
      handleSearch(q);
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, topSuggestions]);

  useEffect(() => {
    const input = document.getElementById("search-box");
    if(autoFocus && input) {
      setTimeout(() => {
        input.focus();
      }, 100)
    }
  }, [])

  const onInputChange = (value) => {
    setSearchQuery(value);
  }

  const handleSearch = (query) => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) {
      setFilteredResults([]);
      return;
    }
    // For short-ish queries prefer startsWith on main fields to reduce matches,
    // then fallback to includes for longer queries.
    const useStarts = q.length <= 3;
    const filtered = normalized.filter(n => {
      if (useStarts) {
        return n.name.startsWith(q) || n.symbol.startsWith(q) || n.pair.startsWith(q) || n.hash.startsWith(q);
      }
      return n.name.includes(q) || n.symbol.includes(q) || n.pair.includes(q) || n.hash.includes(q);
    }).slice(0, MAX_RESULTS).map(n => n.orig);

    setFilteredResults(filtered);
  };

  const navigateTo = (urlTokenHash) => {
    window.location.href = `/callers/${urlTokenHash}`; 
  }

  const onClearClick = () => {
    setSearchQuery('');
  }

  const onCloseClick = () => {
    document.querySelector('.left-close').click();
  }

  return (
    <div className='search-bar'>
      <input
        className='gray-bg gray-border inter-medium-16'
        id="search-box"
        type="text"
        placeholder="Search token name, pair or hash.."
        value={searchQuery}
        onChange={(e) => onInputChange(e.target.value)}
        readOnly={readOnly}
        autoFocus={autoFocus}
      />
      <img src={searchIcon && searchIcon === "gray" ? "/img/search-24-gray.png" : "/img/sgt-search-icon.png" } alt="search-icon" className="search-icon" />
      <span className='clear inter-medium-16 gray-txt' onClick={onClearClick}>clear</span>
      <span className='close'><img onClick={onCloseClick} src="/img/sgt-close.png" alt='close'/></span>
      <div className='search-results'>
        {filteredResults && filteredResults.length > 0 ? (
          <ul className='result-list'>
            {filteredResults.map((item, i) => {
              return (
              <li key={i} onClick={()=> navigateTo(item.TokenHash)} className='flex-box'>
                <div className='flex-box' style={{"gap": "15px"}}>
                  <div>
                    <img src="/img/no-image.png" alt='search' loading="lazy" />
                  </div>
                  <div>
                    <p style={{"marginBottom": "0"}}>{item.TokenName}</p>
                    <span className='inter-medium-16 gray-txt'>{item.TokenSymbol} | <span>{modifiedTokenHash(item.TokenHash)}</span></span>
                  </div>
                </div>
                <div>
                  <p style={{"marginBottom": "0"}}>
                    {item.MarketCap ? item.MarketCap: "$0.00K"}
                    <img src="/img/sgt-right-arrow-gray.png" alt="arrow-right" style={{"width" : "6px", "height": "12px", "marginLeft": "30px"}} /> 
                  </p>
                  <span className='inter-bold-12 gray-txt mkap-res'>M.CAP</span>
                </div>
              </li>
              )
            })}
          </ul>
        ) : searchQuery && (
          <p style={{"marginTop": "20px"}} className='inter-medium-16'>No results found for "{searchQuery}".</p>
        )}
        </div>
      
    </div>
  );
};

// Usage Example
// Assuming `data` is passed as a prop, which can be an array of objects containing title and description

// const mockData = [
//   { id: 1, title: "React Tutorial", description: "Learn the basics of React.js." },
//   { id: 2, title: "JavaScript Tips", description: "Best practices for JavaScript development." },
//   { id: 3, title: "CSS Grid Layout", description: "Mastering CSS Grid for responsive designs." }
// ];

// <SearchComponent data={mockData} />

export default SearchComponent;
